import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import {
  Grid,
  Typography,
  TextField,
  FormControlLabel,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  Radio,
  FormLabel,
  RadioGroup,
  MenuItem,
  Paper,
  CardHeader,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { useQuery } from "@tanstack/react-query";
import * as api from "../actions/api";

// react-tabulator
import "react-tabulator/lib/styles.css"; // required styles
import "react-tabulator/lib/css/tabulator.min.css"; // theme
import { ReactTabulator } from "react-tabulator";
import { useTranslation } from "react-i18next";


const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = (props) => {

  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          className="float-end"
          size="large">
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

function settleTabulatorPromise(maybePromise) {
  if (maybePromise && typeof maybePromise.then === "function") {
    maybePromise.catch(() => {});
  }
}

const ProtocolModal = ({ handleClose, open, addProtocol, formData, ...props }) => {
   const { t } = useTranslation();

  //get DispensingLanguage
  const DispensingLanguage =JSON.parse(localStorage.getItem("DispensingLanguage"));
  const lan = localStorage.getItem('LangCode');
  const menukey = "prescriptions";

  const tblRefHolder = useRef({ table: null });
  const { data } = useQuery(["protocolModal"],
    api.getProtocolListModal,{
      refetchOnWindowFocus: false,
      // enabled: false // turned off by default, manual refetch is needed
    }
  );
  const [selectedProducts, setselectedProducts] = useState([]);


  const ProductColumns = [
    { title: "id", field: "id", visible: false, headerFilter:false },
    { title: "ProtocolId", field: "ProtocolId", visible: false },
    { title: t(DispensingLanguage[lan][menukey]['Sl#']),  formatter: "rownum", width:50, hozAlign: "center" , headerHozAlign:"center", headerSort:false},
    { title: t(DispensingLanguage[lan][menukey]['Regimen']), field: "RegimenName", width: 210, headerFilter:true },
    { title: t(DispensingLanguage[lan][menukey]['Code']), field: "code", width: 140, headerFilter:true },
    { title: t(DispensingLanguage[lan][menukey]['Protocol Name']), field: "protocolName", headerFilter:true  },
  ];

  const ProdustDataItems = data?.map((item, index) => { 
    return item;
  });

  const [btnDisable, setbtnDisable] = useState(false);
  const SaveSelectedProducts = () => {
    const table = tblRefHolder.current?.table;
    if (!table || typeof table.getSelectedData !== "function") return;
    const selectedRows = table.getSelectedData();
    if (selectedRows.length > 0) setbtnDisable(true);
    setselectedProducts(selectedRows);
    setTimeout(() => setbtnDisable(false), 500);
  };

  useEffect(() => {
    addProtocol(selectedProducts);
  }, [selectedProducts]);

  useEffect(() => {
    if (!open || !Array.isArray(data) || data.length === 0) return;

    const rawId = formData?.ExistingProtocolId;
    if (rawId === undefined || rawId === null || rawId === "") return;

    const match = data.find(
      (row) =>
        String(row.id) === String(rawId) ||
        String(row.ProtocolId) === String(rawId)
    );
    if (!match) return;

    const rowId = match.id;
    let cancelled = false;
    const timeouts = [];

    const syncExistingProtocolRow = () => {
      if (cancelled) return;
      const table = tblRefHolder.current?.table;
      if (!table || typeof table.getRow !== "function") return;

      let row;
      try {
        row = table.getRow(rowId);
      } catch {
        return;
      }
      if (row === false || row == null) return;

      try {
        settleTabulatorPromise(table.selectRow?.(rowId));
      } catch {
        /* Tabulator may throw synchronously in edge cases */
      }
      try {
        settleTabulatorPromise(table.scrollToRow?.(rowId, "center", true));
      } catch {
        /* ignore */
      }
    };

    timeouts.push(window.setTimeout(syncExistingProtocolRow, 0));
    timeouts.push(window.setTimeout(syncExistingProtocolRow, 120));
    timeouts.push(window.setTimeout(syncExistingProtocolRow, 400));

    return () => {
      cancelled = true;
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, [open, data, formData?.ExistingProtocolId]);



  return (
    <div>
      <BootstrapDialog
        onClose={() => handleClose('Protocol')}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth={true}
        maxWidth="md"
      >
        <BootstrapDialogTitle
          id="customized-dialog-title"
          onClose={() => handleClose('Protocol')}
        >
        {t(DispensingLanguage[lan][menukey]['Protocol List'])} 
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <ReactTabulator
            onRef={(r) => {
              tblRefHolder.current.table = r.current;
            }}
            options={{
              selectableRowsRangeMode: 'click',
              selectableRows: 1,
            }}
            columns={ProductColumns}
            data={ProdustDataItems}
            height="300px"
            layout={"fitColumns"}

          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={btnDisable}
            className="me-2 float-start"
            variant="contained"
            color="primary"
            onClick={SaveSelectedProducts}
          >
           {t(DispensingLanguage[lan][menukey]['ADD'])}  
          </Button>
          <Button
            autoFocus
            className="float-start"
            variant="contained"
            color="gray"
            onClick={() => handleClose('Protocol')}
          >
            {t(DispensingLanguage[lan][menukey]['Cancel'])}   
          </Button>
          {/* 

          <Button
            autoFocus
            className="float-start"
            variant="contained" 
            onClick={() => handleCloseSelectRow()}
          >
            {'Select Row'}   
          </Button> */}


        </DialogActions>
      </BootstrapDialog>
    </div>
  );
};

export default ProtocolModal;
